import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import config from '../../config';
import {
    clearGoogleSession,
    consumePendingPopup,
    hasEstablishedGoogleSession,
    IFRAME_NAME,
    markGoogleSessionEstablished,
    POPUP_NAME,
    showLoggingInSplash,
} from '../../utils/rpmImpactLaunch';

const RPMImpactTab = ({ patient }) => {
    const formRef = useRef(null);
    const activePopupRef = useRef(null);
    const popupPollTimerRef = useRef(null);
    const autoLaunchAttemptedRef = useRef(false);
    const patientId = patient && patient.id;

    const [needsPopupLaunch, setNeedsPopupLaunch] = useState(() => !hasEstablishedGoogleSession());
    const [launchStatus, setLaunchStatus] = useState('');
    const [showManualLaunch, setShowManualLaunch] = useState(false);

    const submitToIframe = useCallback(() => {
        if (!formRef.current) {
            return;
        }
        formRef.current.target = IFRAME_NAME;
        formRef.current.submit();
    }, []);

    const stopWatchingPopup = useCallback(() => {
        if (popupPollTimerRef.current) {
            clearInterval(popupPollTimerRef.current);
            popupPollTimerRef.current = null;
        }
        activePopupRef.current = null;
    }, []);

    const finishPopupLaunch = useCallback(() => {
        if (activePopupRef.current && !activePopupRef.current.closed) {
            activePopupRef.current.close();
        }
        stopWatchingPopup();
        markGoogleSessionEstablished();
        setNeedsPopupLaunch(false);
        setLaunchStatus('');
        setShowManualLaunch(false);
        submitToIframe();
    }, [stopWatchingPopup, submitToIframe]);

    const startPopupLaunch = useCallback((popup) => {
        if (!popup || !formRef.current) {
            setLaunchStatus('Popup blocked — allow popups for this site and try again.');
            setShowManualLaunch(true);
            return;
        }

        showLoggingInSplash(popup);
        popup.focus();
        formRef.current.target = POPUP_NAME;
        formRef.current.submit();

        activePopupRef.current = popup;
        setLaunchStatus(
            'Complete Google sign-in in the popup. Impact will load below when you close the popup.'
        );
        setShowManualLaunch(false);

        popupPollTimerRef.current = setInterval(() => {
            if (activePopupRef.current && activePopupRef.current.closed) {
                finishPopupLaunch();
            }
        }, 500);
    }, [finishPopupLaunch]);

    const launchViaPopup = useCallback(() => {
        startPopupLaunch(window.open('', POPUP_NAME, 'width=800,height=900'));
    }, [startPopupLaunch]);

    const handleSignInAgain = useCallback((event) => {
        event.preventDefault();
        clearGoogleSession();
        stopWatchingPopup();
        autoLaunchAttemptedRef.current = false;
        setNeedsPopupLaunch(true);
        setLaunchStatus('');
        launchViaPopup();
    }, [launchViaPopup, stopWatchingPopup]);

    useLayoutEffect(() => {
        if (!needsPopupLaunch || patientId == null || !formRef.current || autoLaunchAttemptedRef.current) {
            return;
        }

        autoLaunchAttemptedRef.current = true;
        startPopupLaunch(consumePendingPopup());
    }, [needsPopupLaunch, patientId, startPopupLaunch]);

    useEffect(() => {
        if (patientId == null || !formRef.current || needsPopupLaunch) {
            return;
        }

        const frameId = requestAnimationFrame(() => {
            submitToIframe();
        });

        return () => cancelAnimationFrame(frameId);
    }, [patientId, needsPopupLaunch, submitToIframe]);

    useEffect(() => () => {
        if (popupPollTimerRef.current) {
            clearInterval(popupPollTimerRef.current);
        }
        if (activePopupRef.current && !activePopupRef.current.closed) {
            activePopupRef.current.close();
        }
    }, []);

    if (!patient || patientId == null) {
        return (
            <div style={{ padding: 20, textAlign: 'center' }}>
                Validic enrollment not yet confirmed. iFrame unavailable.
            </div>
        );
    }

    return (
        <div style={{ width: '100%' }}>
            {needsPopupLaunch &&
                <div style={{ padding: '12px 0', borderBottom: '1px solid #BDBDBD', marginBottom: 12 }}>
                    <p style={{ margin: '0 0 12px', color: '#555', fontSize: 14 }}>
                        Google sign-in cannot run inside the embedded panel on first use.
                        A popup opens automatically for authentication, then Impact loads below.
                    </p>
                    {showManualLaunch &&
                        <button
                            type="button"
                            className="btn btn-primary"
                            onClick={launchViaPopup}
                        >
                            Launch Impact
                        </button>
                    }
                    {launchStatus &&
                        <p style={{ margin: showManualLaunch ? '12px 0 0' : 0, fontSize: 14, color: '#333' }}>{launchStatus}</p>
                    }
                </div>
            }

            <form
                key={`rpm-impact-${patientId}`}
                ref={formRef}
                method="GET"
                action={config.VALIDIC_IMPACT_URL}
                target={IFRAME_NAME}
                style={{ display: 'none' }}
            >
                <input type="hidden" name="ccm_patient_id" value={patientId} />
            </form>
            <iframe
                name={IFRAME_NAME}
                title="RPM / Impact"
                style={{
                    border: '1px solid #BDBDBD',
                    width: '100%',
                    minHeight: 600,
                    overflow: 'auto'
                }}
                frameBorder="0"
            />
            {!needsPopupLaunch &&
                <p style={{ margin: '12px 0 20px', padding: '0 4px', fontSize: 13, textAlign: 'right' }}>
                    <a href="#" onClick={handleSignInAgain}>
                        Having trouble loading Impact? Sign in again
                    </a>
                </p>
            }
        </div>
    );
};

export default RPMImpactTab;
